/* global console */

import { notify } from "../components/providers/GlobalToast";

/**
 * JSON Table → Markdown Table Converter
 *
 * Converts proprietary JSON table format into annotated Markdown tables.
 * Bracket notations preserve metadata that Markdown can't express:
 *   [icon]                    — cell has an icon
 *   [shape: Type, #color]     — cell has a shape background
 *   [merged: direction xN]    — cell is the anchor of a merge
 *   *[merged]*                — cell is hidden by a neighboring merge
 *   **text**                  — bold text
 *
 * Input: { structure: { cells[][], columns[], rows[], merged[] }, cellStyles }
 * Merge coordinates use { x: rowIndex, y: colIndex } (x=row, not horizontal).
 * Spacer columns/rows are strings starting with "s" (e.g. "s18", "s24").
 */

const HTML_ENTITY_MAP = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&nbsp;": " ",
};

function isSpacer(value) {
  return typeof value === "string" && value[0] === "s";
}

function stripHtml(html) {
  if (!html || typeof html !== "string") return "";

  return html
    .replace(/<li[^>]*>/gi, "• ")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/p>/gi, " ")
    .replace(/<\/li>/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&\w+;/g, (entity) => HTML_ENTITY_MAP[entity] || entity)
    .replace(/ {2,}/g, " ")
    .trim();
}

function buildShapeTag(shape) {
  if (!shape?.type) return "";
  return shape.color ? `[shape: ${shape.type}, ${shape.color}]` : `[shape: ${shape.type}]`;
}

/**
 * Extracts text with per-segment bold tracking from ProseMirror JSON.
 * Returns an array of { text, bold } segments so only actually-bold
 * portions get wrapped in **.
 */
function extractTextSegments(json) {
  if (!json?.content) return null;

  const segments = [];
  for (const block of json.content) {
    for (const inline of block.content || []) {
      if (!inline.text) continue;
      const bold = (inline.marks || []).some((m) => m.type === "bold");
      segments.push({ text: inline.text, bold });
    }
  }
  return segments.length > 0 ? segments : null;
}

function renderCellText(cell, style) {
  let content = "";

  // Build text with per-segment bold from JSON marks when available
  const segments = extractTextSegments(cell.json);
  if (segments) {
    const isBoldFromStyle = style?.weight === "bold";
    content = segments
      .map((s) => {
        const t = s.text;
        return s.bold || isBoldFromStyle ? `**${t}**` : t;
      })
      .join("");
  } else {
    const text = stripHtml(cell.text || "");
    if (text) {
      content = style?.weight === "bold" ? `**${text}**` : text;
    }
  }

  // Prepend icon/shape tags
  const iconTag = style?.icon ? "[icon]" : "";
  const shapeTag = style?.shape ? buildShapeTag(style.shape) : "";
  const prefix = [iconTag, shapeTag].filter(Boolean).join(" ");

  return prefix ? `${prefix} ${content}`.trim() : content;
}

/**
 * Builds an index remapping array for filtering out spacers.
 * indexMap[i] gives the new contiguous index, or -1 if i is a spacer.
 */
function buildIndexMap(arr) {
  const indexMap = new Array(arr.length);
  let mapped = 0;

  for (let i = 0; i < arr.length; i++) {
    indexMap[i] = isSpacer(arr[i]) ? -1 : mapped++;
  }

  return { indexMap, count: mapped };
}

/**
 * Remaps merge definitions from original grid to filtered grid (after spacer removal).
 * JSON merge length is the number of *extra* cells beyond the anchor, so total span = length + 1.
 */
function remapMerges(merged, rows, cols, rowMap, colMap, filteredNumRows, filteredNumCols) {
  const remapped = [];

  for (const m of merged) {
    const origRow = m.cell.x;
    const origCol = m.cell.y;
    const newRow = rowMap[origRow];
    const newCol = colMap[origCol];

    if (newRow < 0 || newCol < 0) continue;

    const direction = m.dir || "right";
    const totalSpan = m.length + 1;

    // Count how many spacers fall within this merge's full span
    const source = direction === "down" ? rows : cols;
    const start = direction === "down" ? origRow : origCol;
    let spacersInSpan = 0;
    for (let i = 0; i < totalSpan && start + i < source.length; i++) {
      if (isSpacer(source[start + i])) spacersInSpan++;
    }
    const filteredSpan = totalSpan - spacersInSpan;

    const maxSpan = direction === "down" ? filteredNumRows - newRow : filteredNumCols - newCol;
    const realSpan = Math.min(filteredSpan, maxSpan);

    if (realSpan > 1) {
      remapped.push({
        cell: { x: newRow, y: newCol },
        span: realSpan,
        dir: direction,
      });
    }
  }

  return remapped;
}

function buildMergeLookups(merges, numRows, numCols) {
  const starts = {};
  const hidden = new Set();

  for (const m of merges) {
    const { x: row, y: col } = m.cell;
    const { span, dir } = m;

    starts[`${row},${col}`] = `${dir} x${span}`;

    for (let i = 1; i < span; i++) {
      const r = dir === "down" ? row + i : row;
      const c = dir === "down" ? col : col + i;
      if (r < numRows && c < numCols) {
        hidden.add(`${r},${c}`);
      }
    }
  }

  return { starts, hidden };
}

function formatRow(row) {
  // possible multi-line contents still enter this, clean it?
  return `| ${row.join(" | ")} |`;
}

function formatSeparator(numCols) {
  return `| ${new Array(numCols).fill("---").join(" | ")} |`;
}

export function jsonToMarkdown(data) {
  const { cells, merged, columns, rows } = data?.structure ?? {};
  const cellStyles = data?.cellStyles ?? {};

  if (!cells.length || cells.length === 0) {
    console.error("Can't generate AI prompt, data is not formatted properly", data);
    notify("error", "error.prompt.failed");
    return "";
  }

  // buildIndexMap is entirely unnecessary, refactor the below for to process the cells one by one
  const { indexMap: colMap, count: numCols } = buildIndexMap(columns);
  const { indexMap: rowMap, count: numRows } = buildIndexMap(rows);

  const remappedMerges = remapMerges(merged, rows, columns, rowMap, colMap, numRows, numCols);
  // buildMergeLookups is entirely unnecessary, the condition can be checked while iterating on the coordinates
  // alternative: can build a lookup matrix instead based on r,c index, no need for string additions
  const { starts: mergeStarts, hidden: mergeHidden } = buildMergeLookups(remappedMerges, numRows, numCols);

  const tableRows = [];

  for (let r = 0; r < cells.length; r++) {
    if (isSpacer(rows[r])) continue;

    const mdRow = [];
    const mr = rowMap[r];

    for (let c = 0; c < cells[r].length; c++) {
      if (isSpacer(columns[c])) continue;

      const mc = colMap[c]; // cells[r][c] would be equivalent?
      const key = `${mr},${mc}`; // unnecessary

      if (mergeHidden.has(key)) {
        // check based on coordinate, not a text key
        mdRow.push("*[merged]*");
        continue;
      }

      const cell = cells[r][c];
      const style = cellStyles[cell.id] || null;
      let content = renderCellText(cell, style);

      if (mergeStarts[key]) {
        content = `[merged: ${mergeStarts[key]}] ${content}`.trim();
      }

      mdRow.push(content);
    }

    tableRows.push(mdRow);
  }

  // formatRow wastes token count while gives a nicely tabulated table, it is entirely unnecessary for a proper markdown
  const lines = [formatRow(tableRows[0]), formatSeparator(numCols), ...tableRows.slice(1).map((row) => formatRow(row))];

  return lines.join("\n");
}
